import classNames from 'classnames';
import { CSSProperties, FC, ReactNode, useEffect, useRef, useState } from 'react';

interface HorizontalSlideInOutProps {
  children: ReactNode;
  direction: 'forward' | 'backward' | 'manual';
  duration?: number;
  easing?: 'spring-smooth' | 'spring-snappy' | 'spring-bouncy' | 'default';
}

export const HorizontalSlideInOut: FC<HorizontalSlideInOutProps> = ({
  children,
  direction,
  duration = 250,
  easing,
}) => {
  const [slotA, setSlotA] = useState<ReactNode>(children);
  const [slotB, setSlotB] = useState<ReactNode>(null);
  const [activeSlot, setActiveSlot] = useState<'A' | 'B'>('A');
  const [isAnimating, setIsAnimating] = useState(false);
  const [transitionId, setTransitionId] = useState(0);
  const currentChildrenRef = useRef<ReactNode>(children);

  if (children !== currentChildrenRef.current) {
    currentChildrenRef.current = children;
    if (activeSlot === 'A') {
      setSlotB(children);
      setActiveSlot('B');
    } else {
      setSlotA(children);
      setActiveSlot('A');
    }
    setIsAnimating(true);
    setTransitionId((id) => id + 1);
  }

  useEffect(() => {
    if (transitionId === 0) return;

    const timer = setTimeout(() => {
      if (activeSlot === 'A') {
        setSlotB(null);
      } else {
        setSlotA(null);
      }
      setIsAnimating(false);
    }, duration);

    return () => clearTimeout(timer);
  }, [transitionId, duration, activeSlot]);

  const slotAIsActive = activeSlot === 'A';
  const slotAIsExiting = isAnimating && !slotAIsActive;
  const slotAIsEntering = isAnimating && slotAIsActive;

  const slotBIsActive = activeSlot === 'B';
  const slotBIsExiting = isAnimating && !slotBIsActive;
  const slotBIsEntering = isAnimating && slotBIsActive;

  const easingClass = easing ? `motion-ease-${easing}` : '';
  const durationClass = 'motion-duration-[var(--horizontal-slide-duration)]';
  const durationStyle = { '--horizontal-slide-duration': `${duration}ms` } as CSSProperties;

  return (
    <div className="relative w-full min-h-full">
      {slotA !== null && (
        <div
          style={durationStyle}
          className={classNames('absolute w-full h-full', easingClass, durationClass, {
            'motion-translate-x-out-[-100%] motion-opacity-out-0':
              slotAIsExiting && (direction === 'forward' || direction === 'manual'),
            'motion-translate-x-out-100 motion-opacity-out-0': slotAIsExiting && direction === 'backward',
            'motion-translate-x-in-100 motion-opacity-in-0':
              slotAIsEntering && (direction === 'forward' || direction === 'manual'),
            'motion-translate-x-in-[-100%] motion-opacity-in-0': slotAIsEntering && direction === 'backward',
          })}
        >
          {slotA}
        </div>
      )}
      {slotB !== null && (
        <div
          style={durationStyle}
          className={classNames('absolute w-full h-full', easingClass, durationClass, {
            'motion-translate-x-out-[-100%] motion-opacity-out-0':
              slotBIsExiting && (direction === 'forward' || direction === 'manual'),
            'motion-translate-x-out-100 motion-opacity-out-0': slotBIsExiting && direction === 'backward',
            'motion-translate-x-in-100 motion-opacity-in-0':
              slotBIsEntering && (direction === 'forward' || direction === 'manual'),
            'motion-translate-x-in-[-100%] motion-opacity-in-0': slotBIsEntering && direction === 'backward',
          })}
        >
          {slotB}
        </div>
      )}
    </div>
  );
};
